import { getUrlForMedia, TransportStates } from '@arpeggi/shared'
import { cast, Instance, types } from 'mobx-state-tree'
import ArpRegistry from '../../features/arp-registry/ArpRegistry'
import { ForDirectExtend } from '../../features/mobx'

const _GlobalPlayerStore = types
  .model({
    arpMediaIndex: types.maybeNull(types.number),
    transportState: TransportStates.STOPPED,
    state: types.optional(types.enumeration(['loading', 'ready', 'error']), 'ready'),
    volume: 1.0,
    autoplay: true,
    loop: false,
    muted: false,

    // whether the DNA section of the global player should update or not
    // when the selected media is updated
    dnaIsLocked: false,

    // don't try to access the values of the queue/stack directly -- use the helper functions
    queue: types.array(types.number), // the next arp media indexes to play (watch out, this is technically stored in reverse!)
    previousStack: types.array(types.number) // the stack of arp media indexes that previously played
  })
  .actions((self) => {
    const audio = new Audio()

    const afterCreate = () => {
      setArpMediaIndex(null)
      setVolumeInternal()
      addOrRemoveAutoplayEventListener()
    }

    const setArpMediaIndex = async (newArpMediaIndex: number, clearHistory: boolean = false) => {
      if (newArpMediaIndex === undefined) newArpMediaIndex = null

      if (clearHistory) {
        clearQueue()
        clearPreviousStack()
      }

      self.state = 'loading'
      if (self.arpMediaIndex === newArpMediaIndex) {
        self.state = 'ready'
        return
      }
      self.arpMediaIndex = newArpMediaIndex
      if (self.arpMediaIndex === null) {
        stop()
        self.state = 'ready'
        return
      }

      // making sure the arp media index is loaded and the audio is loaded
      const arpMedia = await ArpRegistry.getByIdAsync(self.arpMediaIndex)
      audio.src = encodeURI(getUrlForMedia({ ...arpMedia, index: arpMedia.id }))
      return await new Promise<void>((resolve) => {
        audio.addEventListener('loadedmetadata', () => {
          self.state = 'ready'
          resolve()
        })
      })
    }

    const setDnaIsLocked = (val: boolean) => {
      self.dnaIsLocked = val
    }

    /////////////////// Previous Stack ///////////////////////

    const clearPreviousStack = () => {
      self.previousStack.clear()
    }

    const previousStackPush = (arpId: number) => {
      self.previousStack.push(arpId)
    }

    const previousStackPop = (): number => {
      return self.previousStack.pop()
    }

    /////////////////// Queue ///////////////////////

    const setQueue = (arpMediaIndexes: number[]) => {
      clearQueue()
      clearPreviousStack()
      enqueue(arpMediaIndexes)
    }

    const clearQueue = () => {
      self.queue.clear()
    }

    // puts a value at the beginning of the queue
    const priorityEnqueue = (arpMediaIndex: number) => {
      self.queue.push(arpMediaIndex)
    }

    const enqueue = (arpMediaIndexes: number[]) => {
      self.queue = cast([...arpMediaIndexes.slice().reverse(), ...self.queue])
    }

    const dequeue = (): number => {
      return self.queue.pop()
    }

    // used when every song in the queue is out -- puts all of the songs
    // in the previous stack back onto the queue
    const recycleStackToQueue = async () => {
      self.queue = cast(self.previousStack.toJSON().reverse())
      self.previousStack = cast([])
      await setArpMediaIndex(dequeue())
    }

    /////////////////// Playback ///////////////////////

    const play = async () => {
      if (self.state !== 'ready' || self.arpMediaIndex === null) return
      await audio.play()
      self.transportState = TransportStates.PLAYING
    }

    const pause = () => {
      audio.pause()
      self.transportState = TransportStates.PAUSED
    }

    const stop = () => {
      audio.pause()
      audio.currentTime = 0
      self.transportState = TransportStates.STOPPED
    }

    const playNextArpMedia = async () => {
      const nextIndex = dequeue()
      const currentIndex = self.arpMediaIndex
      previousStackPush(currentIndex)
      // this is the last sound in the queue
      if (!nextIndex) {
        stop()
        await recycleStackToQueue()

        // a hacky way to do this, but doing this changes the pause button
        // to a play button, and resets the audio timeline
        const currentVolume = self.volume
        audio.volume = 0
        setTimeout(() => {
          stop()
          audio.volume = currentVolume
        }, 25)
      } else {
        await setArpMediaIndex(nextIndex)
        if (self.arpMediaIndex !== null) play()
      }
    }

    const playPreviousArpMedia = async () => {
      if (self.previousStack.length === 0) return
      const currentIndex = self.arpMediaIndex
      const previousIndex = previousStackPop()
      await setArpMediaIndex(previousIndex)
      priorityEnqueue(currentIndex)
      if (self.arpMediaIndex !== null) play()
    }

    /////////////////// Volume ///////////////////////

    const setVolumeInternal = () => {
      audio.volume = self.muted ? 0 : self.volume
    }

    const setVolume = (newVolume: number) => {
      if (newVolume < 0 || newVolume > 1.0) return
      self.volume = newVolume
      setVolumeInternal()
    }

    const toggleMute = () => {
      self.muted = !self.muted
      setVolumeInternal()
    }

    /////////////////// Looping/Autoplay ///////////////////////

    const toggleLoop = () => {
      self.loop = !self.loop
      audio.loop = self.loop
    }

    const addOrRemoveAutoplayEventListener = () => {
      if (self.autoplay) {
        audio.addEventListener('ended', playNextArpMedia)
      } else {
        audio.removeEventListener('ended', playNextArpMedia)
      }
    }

    const toggleAutoPlay = () => {
      self.autoplay = !self.autoplay
      addOrRemoveAutoplayEventListener()
    }

    const getAudio = () => audio

    return {
      afterCreate,
      setArpMediaIndex,
      play,
      pause,
      stop,
      getAudio,
      setVolume,
      toggleMute,
      toggleLoop,
      clearQueue,
      setQueue,
      toggleAutoPlay,
      playNextArpMedia,
      playPreviousArpMedia,
      clearPreviousStack,
      setDnaIsLocked
    }
  })

interface GlobalPlayerStoreType extends ForDirectExtend<typeof _GlobalPlayerStore> {}
export const GlobalPlayerStore: GlobalPlayerStoreType = _GlobalPlayerStore

export interface IGlobalPlayerStore extends ForDirectExtend<Instance<typeof GlobalPlayerStore>> {}
